import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Textarea,
  Button,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Icon,
  useToast,
  Spinner,
  Badge,
  Flex,
  IconButton,
  Checkbox,
  Divider,
  Container,
} from '@chakra-ui/react';
import {
  FaFileAlt,
  FaShip,
  FaFilePdf,
  FaPlus,
  FaTrash,
  FaArrowLeft,
  FaEnvelope,
  FaUsers,
  FaAnchor,
  FaBoxOpen,
} from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import { getShipmentDetail, saveBOLDraft, getBOLDraft, getShipmentContainers, saveContainerDetails, getCustomerDetails, getCustomersByIds, getPortNameByLocode } from './services/shipment.service';
import { generateBillOfLadingPDF } from './services/pdf.service';
import { sendBLDraftEmail } from './services/email.service';
import { getStoredUser } from './services/auth.service';
import { FCLExportShipment, BOLDraft } from './types/shipment.types';

// Interface for Bill of Lading data
interface BillOfLadingData {
  id?: string;
  shipment_id?: string;
  shipper_name: string;
  shipper_address: string;
  consignee_name: string;
  consignee_address: string;
  notify_party_name: string;
  notify_party_address: string;
  pre_carriage_by: string;
  place_of_receipt: string;
  delivery_instructions: string;
  vessel: string;
  voyage: string;
  port_of_loading: string;
  port_of_discharge: string;
  place_of_delivery: string;
  marks_and_numbers: string;
  ncm_hs: string;
  package_description: string;
  gross_weight: string;
  measurement: string;
  container_details: Array<{
    container: string;
    type: string;
    tare: string;
    gross_weight: string;
    net_weight: string;
    cbm: string;
    qty: string;
    seal_number: string;
    vgm: string;
  }>;
  freight_details: string;
  prepaid: boolean;
  collect: boolean;
  freight_payable_at: string;
  place_date_of_issue: string;
  number_of_original_bl: string;
  signature: string;
  bl_signature: string;
  shipper?: string;
  consignee?: string;
  notify_party?: string;
  created_at?: string;
  updated_at?: string;
}

const FormSection = ({ icon, title, children }: { icon: any; title: string; children: React.ReactNode }) => (
  <Box>
    <HStack spacing={2} mb={4}>
      <Icon as={icon} color="appleAccent" boxSize={3.5} />
      <Text fontSize="xs" fontWeight="700" color="appleTextSecondary" textTransform="uppercase" letterSpacing="wider">
        {title}
      </Text>
    </HStack>
    {children}
  </Box>
);

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="xs" fontWeight="medium" color="appleTextSecondary" mb={1}>
    {children}
  </Text>
);

interface DraftBillOfLadingTabProps {
  selectedShipment?: {
    id: string;
    brlReference: string;
    [key: string]: any;
  } | null;
  isLoading?: boolean;
  onSave?: () => void;
  onGeneratePDF?: () => void;
  onAutoFill?: () => void;
}

const DraftBillOfLadingTab: React.FC<DraftBillOfLadingTabProps> = ({
  selectedShipment: legacyShipment,
  isLoading: legacyIsLoading,
  onSave: legacyOnSave,
  onGeneratePDF: legacyOnGeneratePDF,
  onAutoFill: legacyOnAutoFill,
}) => {
  const { shipmentId } = useParams<{ shipmentId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  // State management
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [shipment, setShipment] = useState<FCLExportShipment | null>(null);
  const [draftData, setDraftData] = useState<BOLDraft | null>(null);

  // State for Bill of Lading data
  const [billOfLadingData, setBillOfLadingData] = useState<BillOfLadingData>({
    shipper_name: '',
    shipper_address: '',
    consignee_name: '',
    consignee_address: '',
    notify_party_name: '',
    notify_party_address: '',
    pre_carriage_by: '',
    place_of_receipt: '',
    delivery_instructions: '',
    vessel: '',
    voyage: '',
    port_of_loading: '',
    port_of_discharge: '',
    place_of_delivery: '',
    marks_and_numbers: '',
    ncm_hs: '',
    package_description: '',
    gross_weight: '',
    measurement: '',
    container_details: [{
      container: '',
      type: 'FCL',
      tare: '',
      gross_weight: '',
      net_weight: '',
      cbm: '',
      qty: '',
      seal_number: '',
      vgm: ''
    }],
    freight_details: '',
    prepaid: false,
    collect: true,
    freight_payable_at: 'DESTINATION',
    place_date_of_issue: '',
    number_of_original_bl: '3',
    signature: '',
    bl_signature: ''
  });

  /**
   * Load shipment and draft data
   */
  useEffect(() => {
    const loadData = async () => {
      if (!shipmentId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const shipment = await getShipmentDetail(shipmentId);
        
        if (!shipment) {
          toast({
            title: 'Error',
            description: 'Failed to load shipment',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          navigate('/dashboard');
          return;
        }

        console.log('Loaded shipment:', shipment);
        setShipment(shipment);

        // Fetch shipper + consignee from customers table in one query
        console.log('[loadData] shipper_id:', shipment.shipper_id, 'consignee_id:', shipment.consignee_id);
        const customerIds = [shipment.shipper_id, shipment.consignee_id].filter(Boolean) as (number | string)[];
        const customerMap = customerIds.length > 0 ? await getCustomersByIds(customerIds) : {};
        const shipperCustomer = shipment.shipper_id ? customerMap[shipment.shipper_id] ?? null : null;
        const consigneeCustomer = shipment.consignee_id ? customerMap[shipment.consignee_id] ?? null : null;
        console.log('[loadData] customers resolved:', { shipperCustomer, consigneeCustomer });

        // Load containers from containers_exp table
        const containers = await getShipmentContainers(shipmentId);
        console.log('Loaded containers from database:', containers);

        // Try to load existing draft
        const draft = await getBOLDraft(shipmentId);
        console.log('Loaded draft:', draft);

        if (draft && draft.draft_data) {
          let draftData = draft.draft_data as any;
          if (typeof draftData === 'string') {
            try {
              draftData = JSON.parse(draftData);
              console.log('Parsed draft_data from string:', draftData);
            } catch (e) {
              console.error('Failed to parse draft_data:', e);
              draftData = {};
            }
          }

          const hasDraftContent = !!(
            draftData.shipper_name ||
            draftData.consignee_name ||
            draftData.vessel ||
            draftData.port_of_loading ||
            draftData.port_of_discharge ||
            draftData.marks_and_numbers ||
            draftData.package_description
          );

          if (!hasDraftContent) {
            console.log('Draft exists but contains no BL content, auto-filling from shipment');
            await autoFillFromShipment(shipment, containers, shipperCustomer, consigneeCustomer);
          } else {
            setDraftData(draft as BOLDraft);
            
            // Parse container_details if it's a string
            let containerDetails = draftData.container_details;
            if (typeof containerDetails === 'string') {
              try {
                containerDetails = JSON.parse(containerDetails);
                console.log('Parsed container details from string:', containerDetails);
              } catch (e) {
                console.error('Failed to parse container_details:', e);
                containerDetails = [];
              }
            }
          
          // Merge draft data with real container data from database.
          // DB columns provide the authoritative identity (number, type, tare, seal).
          // Cargo values (gross/net weight, cbm, qty, vgm) come from the saved draft
          // first, falling back to whatever the DB has stored for those columns.
          const draftContainerMap = new Map<string, any>();
          if (Array.isArray(containerDetails)) {
            containerDetails.forEach((dc: any) => {
              if (dc.container) draftContainerMap.set(dc.container, dc);
            });
          }

          const finalContainerDetails = containers.length > 0
            ? containers.map((c: any) => {
                const saved = draftContainerMap.get(c.container_number) || {};
                return {
                  container: c.container_number || '',
                  type: c.container_type || 'FCL',
                  tare: c.tare || '',
                  seal_number: c.seal || '',
                  // Prefer what was already typed/saved in the draft;
                  // fall back to any value stored in the containers_exp row.
                  gross_weight: saved.gross_weight ?? c.gross_weight ?? '',
                  net_weight:   saved.net_weight   ?? c.net_weight   ?? '',
                  cbm:          saved.cbm          ?? c.cbm          ?? '',
                  qty:          saved.qty          ?? c.qty          ?? '',
                  vgm:          saved.vgm          ?? c.vgm          ?? '',
                };
              })
            : (Array.isArray(containerDetails) && containerDetails.length > 0
                ? containerDetails
                : [{
                    container: '',
                    type: 'FCL',
                    tare: '',
                    gross_weight: '',
                    net_weight: '',
                    cbm: '',
                    qty: '',
                    seal_number: '',
                    vgm: ''
                  }]);
          
          // Clean delivery_instructions if it contains container data
          let cleanedDeliveryInstructions = draftData.delivery_instructions || '';
          if (cleanedDeliveryInstructions.includes('[CONTAINER_DETAILS]')) {
            cleanedDeliveryInstructions = '';
          }
          
          const shipperAwbInfo = shipperCustomer?.awb_bl_info || shipment.shipper_awb_bl_info || '';
          const consigneeAwbInfo = consigneeCustomer?.awb_bl_info || shipment.consignee_awb_bl_info || '';
          const parsedDraftShipper = parseAwbInfo(draftData.shipper_address || shipperAwbInfo || shipment.shipper_address || '');
          const parsedDraftConsignee = parseAwbInfo(draftData.consignee_address || consigneeAwbInfo || shipment.consignee_address || '');

          const portOfLoadingName = draftData.port_of_loading
            || (shipment.pol ? await getPortNameByLocode(shipment.pol) : '')
            || shipment.port_of_loading || '';
          const portOfDischargeName = draftData.port_of_discharge
            || (shipment.pod ? await getPortNameByLocode(shipment.pod) : '')
            || shipment.port_of_discharge || '';

          const draftDataWithContainers = {
            ...draftData,
            shipper_name: (draftData.shipper_name || parsedDraftShipper.name || shipperCustomer?.name || shipment.shipper_name || ''),
            shipper_address: draftData.shipper_address || parsedDraftShipper.address || shipperAwbInfo || shipment.shipper_address || '',
            consignee_name: (draftData.consignee_name || parsedDraftConsignee.name || consigneeCustomer?.name || shipment.consignee_name || ''),
            consignee_address: draftData.consignee_address || parsedDraftConsignee.address || consigneeAwbInfo || shipment.consignee_address || '',
            notify_party_name: draftData.notify_party_name || shipment.notify_party_name || '',
            notify_party_address: draftData.notify_party_address || shipment.notify_party_address || '',
            pre_carriage_by: draftData.pre_carriage_by || shipment.pre_carriage_by || '',
            place_of_receipt: draftData.place_of_receipt || shipment.place_of_receipt || '',
            delivery_instructions: cleanedDeliveryInstructions || shipment.delivery_instructions || '',
            vessel: (draftData.vessel || shipment.vessel || '').toUpperCase(),
            voyage: draftData.voyage || shipment.voyage || '',
            port_of_loading: portOfLoadingName.toUpperCase(),
            port_of_discharge: portOfDischargeName.toUpperCase(),
            place_of_delivery: draftData.place_of_delivery || shipment.place_of_delivery || '',
            marks_and_numbers: draftData.marks_and_numbers || shipment.marks_and_numbers || shipment.brl_reference || '',
            ncm_hs: draftData.ncm_hs || shipment.ncm_hs || '',
            package_description: draftData.package_description || shipment.package_description || '',
            gross_weight: draftData.gross_weight || shipment.gross_weight || '',
            measurement: draftData.measurement || shipment.measurement || '',
            freight_details: draftData.freight_details || shipment.freight_details || 'AS PER AGREEMENT',
            prepaid: draftData.prepaid ?? shipment.prepaid ?? false,
            collect: draftData.collect ?? shipment.collect ?? true,
            freight_payable_at: draftData.freight_payable_at || shipment.freight_payable_at || ((draftData.prepaid ?? shipment.prepaid) ? 'ORIGIN' : 'DESTINATION'),
            place_date_of_issue: draftData.place_date_of_issue || (() => {
              const now = new Date();
              const day = now.getDate();
              const month = now.toLocaleString('en-US', { month: 'long' }).toUpperCase();
              const year = now.getFullYear();
              return `${portOfLoadingName.toUpperCase()}, ${day} ${month} ${year}`;
            })(),
            number_of_original_bl: draftData.number_of_original_bl || shipment.number_of_original_bl || '3',
            bl_signature: draftData.bl_signature || shipment.bl_signature || '',
            container_details: finalContainerDetails,
          };
          
          console.log('Final BOL data:', draftDataWithContainers);
          setBillOfLadingData(draftDataWithContainers as any);
        }
      } else {
        console.log('No draft found, auto-filling from shipment');
        await autoFillFromShipment(shipment, containers, shipperCustomer, consigneeCustomer);
      }
    } catch (error) {
        console.error('Error loading shipment:', error);
        toast({
          title: 'Error',
          description: 'Failed to load shipment data',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [shipmentId, navigate, toast]);

  /**
   * Auto-fill BOL data from shipment
   */
  // Parse awb_bl_info content into a name and address.
  // Uses first non-empty line as name, rest as address block.
  const parseAwbInfo = (awb: string | undefined | null) => {
    const result = { name: '', address: '' };
    if (!awb) return result;
    const lines = String(awb).split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return result;
    result.name = lines[0];
    if (lines.length > 1) result.address = lines.slice(1).join(', ');
    return result;
  };

  const autoFillFromShipment = async (
    shipmentData: FCLExportShipment,
    containers: any[] = [],
    prefetchedShipper?: { name: string; awb_bl_info: string } | null,
    prefetchedConsignee?: { name: string; awb_bl_info: string } | null,
  ) => {
    if (!shipmentData) return;

    console.log('Auto-filling from shipment data:', shipmentData);
    console.log('Containers for auto-fill:', containers);

    try {
      const [rawShipperDetails, rawConsigneeDetails] = await Promise.all([
        prefetchedShipper
          ? Promise.resolve(prefetchedShipper)
          : shipmentData.shipper_id
          ? getCustomerDetails(shipmentData.shipper_id)
          : Promise.resolve({ name: shipmentData.shipper_name || '', awb_bl_info: shipmentData.shipper_awb_bl_info || shipmentData.shipper_address || '' }),
        prefetchedConsignee
          ? Promise.resolve(prefetchedConsignee)
          : shipmentData.consignee_id
          ? getCustomerDetails(shipmentData.consignee_id)
          : Promise.resolve({ name: shipmentData.consignee_name || '', awb_bl_info: shipmentData.consignee_awb_bl_info || shipmentData.consignee_address || '' }),
      ]);

      const shipperDetails = {
        name: rawShipperDetails.name || shipmentData.shipper_name || '',
        awb_bl_info: rawShipperDetails.awb_bl_info || shipmentData.shipper_address || '',
      };

      const consigneeDetails = {
        name: rawConsigneeDetails.name || shipmentData.consignee_name || '',
        awb_bl_info: rawConsigneeDetails.awb_bl_info || shipmentData.consignee_address || '',
      };

      // Parse awb_bl_info to extract possible name and address lines
      const parsedShipper = parseAwbInfo(shipperDetails.awb_bl_info);
      const parsedConsignee = parseAwbInfo(consigneeDetails.awb_bl_info);

      const portOfLoadingName = shipmentData.pol
        ? await getPortNameByLocode(shipmentData.pol)
        : shipmentData.port_of_loading || shipmentData.origin_port || '';
      const portOfDischargeName = shipmentData.pod
        ? await getPortNameByLocode(shipmentData.pod)
        : shipmentData.port_of_discharge || shipmentData.destination_port || '';

      const containerDetails = containers.length > 0
        ? containers.map((container: any) => ({
            container: container.container_number || '',
            type: 'FCL',
            tare: container.tare || '',
            gross_weight: container.gross_weight || '',
            net_weight: container.net_weight || '',
            cbm: container.cbm || '',
            qty: container.qty || '1',
            seal_number: container.seal_number || container.seal || '',
            vgm: container.vgm || ''
          }))
        : billOfLadingData.container_details;

      setBillOfLadingData(prev => ({
        ...prev,
        shipper_name: (shipperDetails.name || parsedShipper.name).toUpperCase(),
        shipper_address: parsedShipper.address || shipperDetails.awb_bl_info || shipmentData.shipper_address || '',
        shipper: shipperDetails.awb_bl_info || parsedShipper.address || shipmentData.shipper_address || '',
        consignee_name: (consigneeDetails.name || parsedConsignee.name).toUpperCase(),
        consignee_address: parsedConsignee.address || consigneeDetails.awb_bl_info || shipmentData.consignee_address || '',
        consignee: consigneeDetails.awb_bl_info || parsedConsignee.address || shipmentData.consignee_address || '',
        notify_party_name: shipmentData.notify_party_name || '',
        notify_party_address: shipmentData.notify_party_address || '',
        notify_party: shipmentData.notify_party_name || '',
        pre_carriage_by: shipmentData.pre_carriage_by || '',
        place_of_receipt: shipmentData.place_of_receipt || '',
        delivery_instructions: shipmentData.delivery_instructions || shipmentData.special_instructions || '',
        vessel: (shipmentData.vessel || '').toUpperCase(),
        voyage: shipmentData.voyage || '',
        port_of_loading: (portOfLoadingName || '').toUpperCase(),
        port_of_discharge: (portOfDischargeName || '').toUpperCase(),
        place_of_delivery: shipmentData.place_of_delivery || '',
        marks_and_numbers: shipmentData.marks_and_numbers || shipmentData.brl_reference || '',
        ncm_hs: shipmentData.ncm_hs || shipmentData.ncm_hs_code || '',
        package_description: shipmentData.package_description || shipmentData.cargo_description || '',
        gross_weight: shipmentData.gross_weight || '',
        measurement: shipmentData.measurement || '',
        freight_details: shipmentData.freight_details || 'AS PER AGREEMENT',
        prepaid: shipmentData.prepaid ?? false,
        collect: shipmentData.collect ?? true,
        freight_payable_at: shipmentData.freight_payable_at || (shipmentData.prepaid ? 'ORIGIN' : 'DESTINATION'),
        place_date_of_issue: shipmentData.place_date_of_issue || (() => {
          const now = new Date();
          const day = now.getDate();
          const month = now.toLocaleString('en-US', { month: 'long' }).toUpperCase();
          const year = now.getFullYear();
          return `${(portOfLoadingName || '').toUpperCase()}, ${day} ${month} ${year}`;
        })(),
        number_of_original_bl: shipmentData.number_of_original_bl || prev.number_of_original_bl,
        signature: shipmentData.signature || shipmentData.bl_signature || prev.signature || '',
        bl_signature: shipmentData.bl_signature || '',
        container_details: containerDetails,
      }));

      toast({
        title: 'Auto-filled',
        description: 'BOL form populated from shipment data',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error auto-filling bill of lading from shipment:', error);
      toast({
        title: 'Auto-fill failed',
        description: 'Unable to populate Bill of Lading from shipment data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  /**
   * Save BOL draft
   */
  const handleSave = async (markAsSubmitted: boolean = true) => {
    if (!shipment) return;

    setSaving(true);
    try {
      // Prepare BOL data with legacy fields for PDF generation
      const bolDataToSave = {
        ...billOfLadingData,
        // Add legacy fields for PDF generation
        shipper: billOfLadingData.shipper_name,
        consignee: billOfLadingData.consignee_name,
        notify_party: billOfLadingData.notify_party_name,
      };

      // Save BOL draft to bol_drafts table
      const response = await saveBOLDraft(
        shipment.id,
        bolDataToSave,
        markAsSubmitted
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to save draft');
      }

      // Save container details to containers_exp table
      const containerResponse = await saveContainerDetails(
        shipment.id,
        billOfLadingData.container_details
      );

      if (!containerResponse.success) {
        console.warn('Failed to save container details:', containerResponse.message);
        // Don't fail the entire save if containers fail
      }

      // Update local shipment state if marked as submitted
      if (markAsSubmitted && shipment) {
        setShipment({
          ...shipment,
          draft_submitted: true,
          draft_date: new Date().toISOString(),
        });
      }

      toast({
        title: markAsSubmitted ? 'Submitted' : 'Saved',
        description: markAsSubmitted 
          ? 'Draft submitted successfully. You can now generate PDF.'
          : 'Draft and container details saved successfully',
        status: 'success',
        duration: 3000,
      });

      // Navigate back to dashboard after successful submission
      if (markAsSubmitted) {
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500); // Allow time for toast to be seen
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      toast({
        title: 'Error',
        description: 'Failed to save draft',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Generate PDF
   */
  const handleGeneratePDF = () => {
    toast({
      title: 'PDF Generation',
      description: 'PDF generation feature coming soon',
      status: 'info',
      duration: 3000,
    });
  };

  const handleSendByEmail = async () => {
    if (!shipment) return;
    setSending(true);
    try {
      const user = getStoredUser();
      if (!user?.email) throw new Error('User email not found');

      const containers = await getShipmentContainers(shipment.id);
      const mergedData = {
        ...shipment,
        ...billOfLadingData,
        brl_reference: shipment.brl_reference || shipment.shipment_number,
      };

      const pdfBase64 = await generateBillOfLadingPDF(mergedData as any, containers, { returnBase64: true });
      if (!pdfBase64) throw new Error('Failed to generate PDF');

      const result = await sendBLDraftEmail({
        toEmail: user.email,
        shipmentRef: shipment.brl_reference || shipment.shipment_number,
        vessel: billOfLadingData.vessel,
        portOfLoading: billOfLadingData.port_of_loading,
        portOfDischarge: billOfLadingData.port_of_discharge,
        pdfBase64: pdfBase64 as string,
      });

      if (!result.success) throw new Error(result.error);

      toast({
        title: 'Email sent',
        description: `Draft BL sent to ${user.email}`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (err: any) {
      toast({
        title: 'Failed to send email',
        description: err.message || 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  /**
   * Go back to dashboard
   */
  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  // Handler for container detail changes
  const handleContainerDetailChange = (index: number, field: string, value: string) => {
    setBillOfLadingData(prev => {
      const updatedContainers = prev.container_details.map((container, i) => {
        if (i === index) {
          const updatedContainer = { ...container, [field]: value };
          
          // Auto-calculate VGM when tare or gross_weight changes
          if (field === 'tare' || field === 'gross_weight') {
            const tare = parseFloat(field === 'tare' ? value : updatedContainer.tare) || 0;
            const grossWeight = parseFloat(field === 'gross_weight' ? value : updatedContainer.gross_weight) || 0;
            updatedContainer.vgm = (tare + grossWeight).toString();
          }
          
          return updatedContainer;
        }
        return container;
      });
      
      return {
        ...prev,
        container_details: updatedContainers
      };
    });
  };

  // Handler to add a new container
  const handleAddContainer = () => {
    setBillOfLadingData(prev => ({
      ...prev,
      container_details: [
        ...prev.container_details,
        {
          container: `CONT-${prev.container_details.length + 1}`,
          type: 'FCL',
          tare: '2000', // Default tare weight
          gross_weight: '',
          net_weight: '',
          cbm: '',
          qty: '',
          seal_number: `SEAL-${prev.container_details.length + 1}`,
          vgm: ''
        }
      ]
    }));
  };

  // Handler to remove a container
  const handleRemoveContainer = (index: number) => {
    if (billOfLadingData.container_details.length > 1) {
      setBillOfLadingData(prev => ({
        ...prev,
        container_details: prev.container_details.filter((_, i) => i !== index)
      }));
    }
  };

  // Check for legacy props (backward compatibility)
  const isLegacyMode = !shipmentId && legacyShipment;
  const effectiveShipment = isLegacyMode ? legacyShipment : shipment;
  const effectiveLoading = isLegacyMode ? (legacyIsLoading || false) : loading;

  if (loading && !isLegacyMode) {
    return (
      <Flex minH="100vh" align="center" justify="center">
        <VStack spacing={4}>
          <Spinner size="xl" color="appleAccent" thickness="4px" />
          <Text color="appleTextSecondary">Loading shipment...</Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box bg="appleBg" minH="100vh" py={10}>
      <Container maxW="container.lg">
        {!effectiveShipment ? (
          <Box textAlign="center" py={20}>
            <Icon as={FaFileAlt} boxSize={12} color="appleTextSecondary" mb={4} />
            <Text color="appleTextSecondary" fontSize="lg" mb={2}>Select a shipment to create Bill of Lading</Text>
            <Text color="appleTextSecondary" fontSize="sm">
              Please select a shipment from the dashboard to continue
            </Text>
            <Button
              mt={4}
              leftIcon={<Icon as={FaArrowLeft} />}
              colorScheme="blue"
              onClick={handleBackToDashboard}
            >
              Back to Dashboard
            </Button>
          </Box>
        ) : (
          <VStack spacing={8} align="stretch">
            {/* Header */}
            <Flex
              justify="space-between"
              align="center"
              pb={6}
              borderBottom="1px solid"
              borderColor="appleCardBorder"
              wrap="wrap"
              gap={4}
            >
              <HStack spacing={3}>
                <IconButton
                  aria-label="Back to dashboard"
                  icon={<FaArrowLeft />}
                  size="sm"
                  variant="ghost"
                  onClick={handleBackToDashboard}
                />
                <Box>
                  <HStack spacing={3}>
                    <Text fontSize="28px" fontWeight="600" lineHeight="1.14" color="appleTextPrimary">
                      {isLegacyMode
                        ? (effectiveShipment as any).brlReference
                        : shipment?.shipment_number}
                    </Text>
                    {draftData && (
                      <Badge colorScheme="blue" fontSize="xs" borderRadius="full" px={2}>
                        Draft Saved
                      </Badge>
                    )}
                  </HStack>
                  <Text fontSize="sm" color="appleTextSecondary">
                    Draft of Bill of Lading — FCL/FCL
                  </Text>
                </Box>
              </HStack>
              <HStack spacing={2}>
                <Button
                  leftIcon={<Icon as={FaFileAlt} />}
                  colorScheme="blue"
                  size="sm"
                  onClick={() => handleSave(true)}
                  isLoading={saving}
                  loadingText="Submitting..."
                >
                  Submit Draft
                </Button>
                <Button
                  leftIcon={<Icon as={FaFilePdf} />}
                  variant="outline"
                  size="sm"
                  onClick={handleGeneratePDF}
                  isDisabled={!shipment?.draft_submitted}
                >
                  Draft PDF
                </Button>
                <Button
                  leftIcon={<Icon as={FaEnvelope} />}
                  variant="outline"
                  size="sm"
                  onClick={handleSendByEmail}
                  isLoading={sending}
                  loadingText="Sending..."
                  isDisabled={!shipment?.draft_submitted}
                >
                  Send by Email
                </Button>
              </HStack>
            </Flex>

            {/* Form */}
            <VStack spacing={8} align="stretch" divider={<Divider borderColor="appleCardBorder" />}>

              {/* Parties */}
              <FormSection icon={FaUsers} title="Parties">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
                  <Box>
                    <FieldLabel>Shipper</FieldLabel>
                    <VStack spacing={2} align="stretch">
                      <Input
                        variant="flushed"
                        borderColor="appleCardBorder"
                        focusBorderColor="appleAccent"
                        placeholder="Shipper name"
                        value={billOfLadingData.shipper_name}
                        onChange={(e) => setBillOfLadingData(prev => ({ ...prev, shipper_name: e.target.value }))}
                        size="sm"
                      />
                      <Textarea
                        variant="flushed"
                        borderColor="appleCardBorder"
                        focusBorderColor="appleAccent"
                        placeholder="Shipper address"
                        value={billOfLadingData.shipper_address}
                        onChange={(e) => setBillOfLadingData(prev => ({ ...prev, shipper_address: e.target.value }))}
                        rows={3}
                        size="sm"
                      />
                    </VStack>
                  </Box>
                  <Box>
                    <FieldLabel>Consignee</FieldLabel>
                    <VStack spacing={2} align="stretch">
                      <Input
                        variant="flushed"
                        borderColor="appleCardBorder"
                        focusBorderColor="appleAccent"
                        placeholder="Consignee name"
                        value={billOfLadingData.consignee_name}
                        onChange={(e) => setBillOfLadingData(prev => ({ ...prev, consignee_name: e.target.value }))}
                        size="sm"
                      />
                      <Textarea
                        variant="flushed"
                        borderColor="appleCardBorder"
                        focusBorderColor="appleAccent"
                        placeholder="Consignee address"
                        value={billOfLadingData.consignee_address}
                        onChange={(e) => setBillOfLadingData(prev => ({ ...prev, consignee_address: e.target.value }))}
                        rows={3}
                        size="sm"
                      />
                    </VStack>
                  </Box>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <Box>
                    <HStack justify="space-between" align="center" mb={1}>
                      <FieldLabel>Notify Party</FieldLabel>
                      <Button
                        size="xs"
                        variant="ghost"
                        color="appleAccent"
                        onClick={() => {
                          setBillOfLadingData(prev => ({
                            ...prev,
                            notify_party_name: prev.consignee_name,
                            notify_party_address: prev.consignee_address
                          }));
                        }}
                      >
                        Copy from Consignee
                      </Button>
                    </HStack>
                    <VStack spacing={2} align="stretch">
                      <Input
                        variant="flushed"
                        borderColor="appleCardBorder"
                        focusBorderColor="appleAccent"
                        placeholder="Notify party name"
                        value={billOfLadingData.notify_party_name}
                        onChange={(e) => setBillOfLadingData(prev => ({ ...prev, notify_party_name: e.target.value }))}
                        size="sm"
                      />
                      <Textarea
                        variant="flushed"
                        borderColor="appleCardBorder"
                        focusBorderColor="appleAccent"
                        placeholder="Notify party address"
                        value={billOfLadingData.notify_party_address}
                        onChange={(e) => setBillOfLadingData(prev => ({ ...prev, notify_party_address: e.target.value }))}
                        rows={3}
                        size="sm"
                      />
                    </VStack>
                  </Box>
                  <Box>
                    <FieldLabel>For delivery of the goods apply to</FieldLabel>
                    <Textarea
                      variant="flushed"
                      borderColor="appleCardBorder"
                      focusBorderColor="appleAccent"
                      placeholder="Delivery instructions"
                      value={billOfLadingData.delivery_instructions}
                      onChange={(e) => setBillOfLadingData(prev => ({ ...prev, delivery_instructions: e.target.value }))}
                      rows={5}
                      size="sm"
                    />
                  </Box>
                </SimpleGrid>
              </FormSection>

              {/* Route & Vessel */}
              <FormSection icon={FaAnchor} title="Route & Vessel">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <VStack spacing={4} align="stretch">
                    <Box>
                      <FieldLabel>Pre-carriage by</FieldLabel>
                      <Input
                        variant="flushed"
                        borderColor="appleCardBorder"
                        focusBorderColor="appleAccent"
                        placeholder="Pre-carriage method"
                        value={billOfLadingData.pre_carriage_by}
                        onChange={(e) => setBillOfLadingData(prev => ({ ...prev, pre_carriage_by: e.target.value }))}
                        size="sm"
                      />
                    </Box>
                    <Box>
                      <FieldLabel>Port of loading</FieldLabel>
                      <Input
                        variant="flushed"
                        borderColor="appleCardBorder"
                        focusBorderColor="appleAccent"
                        placeholder="Port of loading"
                        value={billOfLadingData.port_of_loading}
                        onChange={(e) => setBillOfLadingData(prev => ({ ...prev, port_of_loading: e.target.value }))}
                        size="sm"
                      />
                    </Box>
                    <Box>
                      <FieldLabel>Vessel | Voyage</FieldLabel>
                      <HStack spacing={2}>
                        <Input
                          variant="flushed"
                          borderColor="appleCardBorder"
                          focusBorderColor="appleAccent"
                          placeholder="Vessel"
                          value={billOfLadingData.vessel}
                          onChange={(e) => setBillOfLadingData(prev => ({ ...prev, vessel: e.target.value }))}
                          size="sm"
                        />
                        <Input
                          variant="flushed"
                          borderColor="appleCardBorder"
                          focusBorderColor="appleAccent"
                          placeholder="Voyage"
                          value={billOfLadingData.voyage}
                          onChange={(e) => setBillOfLadingData(prev => ({ ...prev, voyage: e.target.value }))}
                          size="sm"
                        />
                      </HStack>
                    </Box>
                  </VStack>
                  <VStack spacing={4} align="stretch">
                    <Box>
                      <FieldLabel>Place of receipt by pre-carrier</FieldLabel>
                      <Input
                        variant="flushed"
                        borderColor="appleCardBorder"
                        focusBorderColor="appleAccent"
                        placeholder="Place of receipt"
                        value={billOfLadingData.place_of_receipt}
                        onChange={(e) => setBillOfLadingData(prev => ({ ...prev, place_of_receipt: e.target.value }))}
                        size="sm"
                      />
                    </Box>
                    <Box>
                      <FieldLabel>Port of discharge</FieldLabel>
                      <Input
                        variant="flushed"
                        borderColor="appleCardBorder"
                        focusBorderColor="appleAccent"
                        placeholder="Port of discharge"
                        value={billOfLadingData.port_of_discharge}
                        onChange={(e) => setBillOfLadingData(prev => ({ ...prev, port_of_discharge: e.target.value }))}
                        size="sm"
                      />
                    </Box>
                    <Box>
                      <FieldLabel>Place of delivery by on-carrier</FieldLabel>
                      <Input
                        variant="flushed"
                        borderColor="appleCardBorder"
                        focusBorderColor="appleAccent"
                        placeholder="Place of delivery"
                        value={billOfLadingData.place_of_delivery}
                        onChange={(e) => setBillOfLadingData(prev => ({ ...prev, place_of_delivery: e.target.value }))}
                        size="sm"
                      />
                    </Box>
                  </VStack>
                </SimpleGrid>
              </FormSection>

              {/* Cargo */}
              <FormSection icon={FaBoxOpen} title="Cargo">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
                  <Box>
                    <FieldLabel>Marks and Nos.</FieldLabel>
                    <Textarea
                      variant="flushed"
                      borderColor="appleCardBorder"
                      focusBorderColor="appleAccent"
                      placeholder="Marks and numbers"
                      value={billOfLadingData.marks_and_numbers}
                      onChange={(e) => setBillOfLadingData(prev => ({ ...prev, marks_and_numbers: e.target.value }))}
                      rows={5}
                      size="sm"
                    />
                  </Box>
                  <Box>
                    <FieldLabel>Number, kind of packages and description of goods</FieldLabel>
                    <Textarea
                      variant="flushed"
                      borderColor="appleCardBorder"
                      focusBorderColor="appleAccent"
                      placeholder="Package description and goods details"
                      value={billOfLadingData.package_description}
                      onChange={(e) => setBillOfLadingData(prev => ({ ...prev, package_description: e.target.value }))}
                      rows={5}
                      size="sm"
                    />
                  </Box>
                </SimpleGrid>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                  <Box>
                    <FieldLabel>NCM/HS Code</FieldLabel>
                    <Input
                      variant="flushed"
                      borderColor="appleCardBorder"
                      focusBorderColor="appleAccent"
                      placeholder="NCM/HS code"
                      value={billOfLadingData.ncm_hs}
                      onChange={(e) => setBillOfLadingData(prev => ({ ...prev, ncm_hs: e.target.value }))}
                      size="sm"
                    />
                  </Box>
                  <Box>
                    <FieldLabel>Gross weight</FieldLabel>
                    <Input
                      variant="flushed"
                      borderColor="appleCardBorder"
                      focusBorderColor="appleAccent"
                      placeholder="Gross weight"
                      value={billOfLadingData.gross_weight}
                      onChange={(e) => setBillOfLadingData(prev => ({ ...prev, gross_weight: e.target.value }))}
                      size="sm"
                    />
                  </Box>
                  <Box>
                    <FieldLabel>Measurement</FieldLabel>
                    <Input
                      variant="flushed"
                      borderColor="appleCardBorder"
                      focusBorderColor="appleAccent"
                      placeholder="Measurement"
                      value={billOfLadingData.measurement}
                      onChange={(e) => setBillOfLadingData(prev => ({ ...prev, measurement: e.target.value }))}
                      size="sm"
                    />
                  </Box>
                </SimpleGrid>
              </FormSection>

              {/* Containers */}
              <FormSection icon={FaShip} title={`Containers (${billOfLadingData.container_details.length})`}>
                <Flex justify="flex-end" mb={3}>
                  <Button
                    leftIcon={<Icon as={FaPlus} />}
                    variant="outline"
                    colorScheme="blue"
                    size="sm"
                    onClick={handleAddContainer}
                  >
                    Add Container
                  </Button>
                </Flex>

                <Box overflowX="auto">
                  <Table
                    variant="simple"
                    size="sm"
                    sx={{ 'th, td': { borderColor: 'appleCardBorder' } }}
                  >
                    <Thead>
                      <Tr>
                        {['Container', 'Type', 'Tare', 'Gross', 'Net Weight', 'CBM', 'Qty', 'Seal', 'VGM', ''].map(h => (
                          <Th key={h} fontSize="10px" px={2} color="appleTextSecondary">{h}</Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {billOfLadingData.container_details.map((container, index) => (
                        <Tr key={index}>
                          <Td px={1}>
                            <Input
                              variant="flushed"
                              borderColor="appleCardBorder"
                              focusBorderColor="appleAccent"
                              placeholder="Container"
                              value={container.container}
                              onChange={(e) => handleContainerDetailChange(index, 'container', e.target.value)}
                              size="xs"
                            />
                          </Td>
                          <Td px={1}>
                            <Input
                              variant="flushed"
                              borderColor="appleCardBorder"
                              focusBorderColor="appleAccent"
                              placeholder="Type"
                              value={container.type}
                              onChange={(e) => handleContainerDetailChange(index, 'type', e.target.value)}
                              size="xs"
                            />
                          </Td>
                          <Td px={1}>
                            <Input
                              variant="flushed"
                              borderColor="appleCardBorder"
                              focusBorderColor="appleAccent"
                              placeholder="Tare"
                              value={container.tare}
                              onChange={(e) => handleContainerDetailChange(index, 'tare', e.target.value)}
                              size="xs"
                            />
                          </Td>
                          <Td px={1}>
                            <Input
                              variant="flushed"
                              borderColor="appleCardBorder"
                              focusBorderColor="appleAccent"
                              placeholder="Gross"
                              value={container.gross_weight}
                              onChange={(e) => handleContainerDetailChange(index, 'gross_weight', e.target.value)}
                              size="xs"
                            />
                          </Td>
                          <Td px={1}>
                            <Input
                              variant="flushed"
                              borderColor="appleCardBorder"
                              focusBorderColor="appleAccent"
                              placeholder="Net"
                              value={container.net_weight}
                              onChange={(e) => handleContainerDetailChange(index, 'net_weight', e.target.value)}
                              size="xs"
                            />
                          </Td>
                          <Td px={1}>
                            <Input
                              variant="flushed"
                              borderColor="appleCardBorder"
                              focusBorderColor="appleAccent"
                              placeholder="CBM"
                              value={container.cbm}
                              onChange={(e) => handleContainerDetailChange(index, 'cbm', e.target.value)}
                              size="xs"
                            />
                          </Td>
                          <Td px={1}>
                            <Input
                              variant="flushed"
                              borderColor="appleCardBorder"
                              focusBorderColor="appleAccent"
                              placeholder="Qty"
                              value={container.qty}
                              onChange={(e) => handleContainerDetailChange(index, 'qty', e.target.value)}
                              size="xs"
                            />
                          </Td>
                          <Td px={1}>
                            <Input
                              variant="flushed"
                              borderColor="appleCardBorder"
                              focusBorderColor="appleAccent"
                              placeholder="Seal"
                              value={container.seal_number}
                              onChange={(e) => handleContainerDetailChange(index, 'seal_number', e.target.value)}
                              size="xs"
                            />
                          </Td>
                          <Td px={1}>
                            <Input
                              variant="flushed"
                              borderColor="appleCardBorder"
                              placeholder="VGM"
                              value={(() => {
                                const tare = parseFloat(container.tare) || 0;
                                const grossWeight = parseFloat(container.gross_weight) || 0;
                                const vgm = tare + grossWeight;
                                return vgm > 0 ? vgm.toString() : '';
                              })()}
                              size="xs"
                              isReadOnly
                              color="appleTextSecondary"
                              fontWeight="medium"
                            />
                          </Td>
                          <Td px={1}>
                            <IconButton
                              aria-label="Remove container"
                              icon={<Icon as={FaTrash} />}
                              colorScheme="red"
                              size="xs"
                              variant="ghost"
                              onClick={() => handleRemoveContainer(index)}
                              isDisabled={billOfLadingData.container_details.length === 1}
                              title={billOfLadingData.container_details.length === 1 ? "Cannot remove the last container" : "Remove container"}
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </FormSection>

              {/* Freight & Terms */}
              <FormSection icon={FaFileAlt} title="Freight & Terms">
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                  <VStack align="stretch" spacing={3}>
                    <FieldLabel>Freight details, charges, etc.</FieldLabel>
                    <Textarea
                      variant="flushed"
                      borderColor="appleCardBorder"
                      focusBorderColor="appleAccent"
                      placeholder="Freight details and charges"
                      value={billOfLadingData.freight_details}
                      onChange={(e) => setBillOfLadingData(prev => ({ ...prev, freight_details: e.target.value }))}
                      rows={3}
                      size="sm"
                    />
                    <Text fontSize="xs" color="appleTextSecondary" mt={2}>
                      Excess value declaration: refer to clause 7E on reverse side.
                      Applicable only when document used as a Through Bill of Lading.
                    </Text>
                  </VStack>
                  <VStack align="stretch" spacing={3}>
                    <FieldLabel>Payment terms</FieldLabel>
                    <HStack spacing={6}>
                      <Checkbox
                        colorScheme="blue"
                        isChecked={billOfLadingData.prepaid}
                        onChange={(e) => setBillOfLadingData(prev => ({
                          ...prev,
                          prepaid: e.target.checked,
                          collect: e.target.checked ? false : prev.collect,
                          freight_payable_at: e.target.checked ? 'Origin' : prev.freight_payable_at
                        }))}
                      >
                        <Text fontSize="sm">Prepaid</Text>
                      </Checkbox>
                      <Checkbox
                        colorScheme="blue"
                        isChecked={billOfLadingData.collect}
                        onChange={(e) => setBillOfLadingData(prev => ({
                          ...prev,
                          collect: e.target.checked,
                          prepaid: e.target.checked ? false : prev.prepaid,
                          freight_payable_at: e.target.checked ? 'Destination' : prev.freight_payable_at
                        }))}
                      >
                        <Text fontSize="sm">Collect</Text>
                      </Checkbox>
                    </HStack>
                    <Box>
                      <FieldLabel>Freight payable at</FieldLabel>
                      <Input
                        variant="flushed"
                        borderColor="appleCardBorder"
                        focusBorderColor="appleAccent"
                        placeholder="Freight payable location"
                        value={billOfLadingData.freight_payable_at}
                        onChange={(e) => setBillOfLadingData(prev => ({ ...prev, freight_payable_at: e.target.value }))}
                        size="sm"
                      />
                    </Box>
                    <Box>
                      <FieldLabel>Number of original B/L</FieldLabel>
                      <Input
                        variant="flushed"
                        borderColor="appleCardBorder"
                        focusBorderColor="appleAccent"
                        placeholder="Number of original B/L"
                        value={billOfLadingData.number_of_original_bl}
                        onChange={(e) => setBillOfLadingData(prev => ({ ...prev, number_of_original_bl: e.target.value }))}
                        size="sm"
                      />
                    </Box>
                  </VStack>
                  <VStack align="stretch" spacing={3}>
                    <FieldLabel>Other information</FieldLabel>
                    <Box>
                      <FieldLabel>Place and date of issue</FieldLabel>
                      <Input
                        variant="flushed"
                        borderColor="appleCardBorder"
                        focusBorderColor="appleAccent"
                        placeholder="Place and date of issue"
                        value={billOfLadingData.place_date_of_issue}
                        onChange={(e) => setBillOfLadingData(prev => ({ ...prev, place_date_of_issue: e.target.value }))}
                        size="sm"
                      />
                    </Box>
                    <Box>
                      <FieldLabel>Signature</FieldLabel>
                      <Input
                        variant="flushed"
                        borderColor="appleCardBorder"
                        focusBorderColor="appleAccent"
                        placeholder="Authorized signature"
                        value={billOfLadingData.signature}
                        onChange={(e) => setBillOfLadingData(prev => ({ ...prev, signature: e.target.value }))}
                        size="sm"
                      />
                    </Box>
                  </VStack>
                </SimpleGrid>
              </FormSection>

            </VStack>
          </VStack>
        )}
      </Container>
    </Box>
  );
};

export default DraftBillOfLadingTab;
